import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Film, ImagePlus, Loader2, X, Zap } from "lucide-react";
import { api, assetUrl, errMsg } from "../../lib/api";
import { clipCost } from "../../hooks/useVideoStudio";

const QUALITIES = [["fast", "Fast", "Quicker and cheaper", Zap], ["cinematic", "Cinematic", "Most realistic, takes longer", Film]];

const PhotoPicker = ({ photo, onPick, onClear, idp }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try { const a = await api.upload(f); onPick({ id: a.id, url: a.url }); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  if (photo) return (
    <div className="relative w-20 h-20 rounded-lg overflow-hidden border border-slate-200" data-testid={`${idp}-photo-preview`}>
      <img src={assetUrl(photo.url)} alt="Start frame" className="w-full h-full object-cover" />
      <button onClick={onClear} data-testid={`${idp}-photo-remove`} aria-label="Remove photo"
        className="absolute top-1 right-1 w-5 h-5 rounded-full bg-slate-900/70 text-white flex items-center justify-center hover:bg-slate-900"><X size={11} /></button>
    </div>
  );
  return (
    <>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={pick} data-testid={`${idp}-photo-input`} />
      <button onClick={() => ref.current?.click()} disabled={busy} data-testid={`${idp}-photo-btn`}
        className="h-9 px-3 rounded-lg border border-dashed border-slate-300 text-xs text-slate-600 flex items-center gap-1.5 hover:border-emerald-400 hover:text-emerald-700 transition-colors disabled:opacity-60">
        {busy ? <Loader2 size={13} className="animate-spin" /> : <ImagePlus size={13} />} Start from a photo (optional)
      </button>
    </>
  );
};

export const ClipFields = ({ value, setValue, pricing, idp }) => {
  const set = (k, v) => setValue((o) => ({ ...o, [k]: v }));
  return (
    <div className="space-y-3">
      <textarea value={value.prompt} onChange={(e) => set("prompt", e.target.value)} maxLength={1500} rows={3} data-testid={`${idp}-prompt`}
        placeholder="Describe the scene — e.g. Slow drone shot over a misty tea garden at sunrise"
        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 resize-none" />
      <PhotoPicker photo={value.photo} onPick={(p) => set("photo", p)} onClear={() => set("photo", null)} idp={idp} />
      <div className="grid grid-cols-2 gap-2">
        {QUALITIES.map(([q, label, hint, Icon]) => (
          <button key={q} onClick={() => set("quality", q)} data-testid={`${idp}-quality-${q}`}
            className={`text-left rounded-xl border px-3 py-2.5 transition-colors ${value.quality === q ? "border-emerald-500 bg-emerald-50/60 ring-1 ring-emerald-500/30" : "border-slate-200 bg-white hover:border-slate-300"}`}>
            <span className="flex items-center gap-1.5 text-sm font-medium text-slate-900"><Icon size={13} className="text-emerald-600" />{label}</span>
            <span className="block text-[11px] text-slate-500 mt-0.5">{hint}</span>
            <span className="block text-[11px] text-slate-400 mt-1 font-mono">{clipCost(pricing, q, 5)} cr / 5s</span>
          </button>
        ))}
      </div>
      <div className="flex items-center gap-2 text-xs text-slate-500">
        Length
        {[5, 10].map((d) => (
          <button key={d} onClick={() => set("duration", d)} data-testid={`${idp}-duration-${d}`}
            className={`h-7 px-3 rounded-full border transition-colors ${value.duration === d ? "border-emerald-500 bg-emerald-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}>{d} sec</button>
        ))}
      </div>
    </div>
  );
};

export default ClipFields;
